import { FC, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { BrokerOnboardingRequestDto } from "../../../api/Api";
import { BrokerForm } from "../../brokers/brokerForm";
import {
  useApproveBrokerOnboardingRequest,
  useRejectBrokerOnboardingRequest
} from "./queries";

type Props = {
  open: boolean;
  request: BrokerOnboardingRequestDto | null;
  onClose: () => void;
};

const getMutationError = (error: unknown) => {
  if (!error) return null;
  if (error instanceof Error) return error.message;
  if (typeof error === "object" && "statusText" in error) {
    return String((error as { statusText?: string }).statusText);
  }
  return "Request failed.";
};

export const ReviewDialog: FC<Props> = ({ open, request, onClose }) => {
  const [comment, setComment] = useState("");

  useEffect(() => {
    setComment(request?.reviewerComment ?? "");
  }, [request]);

  const approve = useApproveBrokerOnboardingRequest({ onSuccess: onClose });
  const reject = useRejectBrokerOnboardingRequest({ onSuccess: onClose });
  const isSubmitting = approve.isPending || reject.isPending;
  const isPendingRequest = request?.status === "Pending";
  const trimmedComment = comment.trim();
  const errorMessage = useMemo(
    () => getMutationError(approve.error ?? reject.error),
    [approve.error, reject.error]
  );

  if (!request) {
    return null;
  }

  return (
    <Dialog maxWidth="md" fullWidth open={open} onClose={onClose}>
      <DialogTitle>
        <Stack spacing={0.5}>
          <Typography variant="h5">{`Onboarding request for ${request.payload?.brokerName}`}</Typography>
          <Typography variant="body2" color="text.secondary">
            {`by ${request.requestedByDisplayName ?? ""} - ${request.requestedBy ?? ""}`}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {errorMessage && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <TextField
          label="Reviewer comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          fullWidth
          multiline
          rows={2}
          sx={{ marginBottom: 1 }}
        />

        <BrokerForm
          initialValues={request.payload}
          onSubmit={(payload) => {
            if (!request.id) return;
            approve.mutate({
              id: request.id,
              body: {
                payload,
                comment: trimmedComment || undefined
              }
            });
          }}
          submitting={isSubmitting}
          submitDisabled={!isPendingRequest}
          submitLabel={isPendingRequest ? "Approve and create" : "Closed"}
          onCancel={onClose}
          secondaryAction={
            isPendingRequest ? (
              <Button
                variant="outlined"
                color="error"
                disabled={!trimmedComment || isSubmitting || !request.id}
                onClick={() => {
                  if (!request.id || !trimmedComment) return;
                  reject.mutate({
                    id: request.id,
                    body: { comment: trimmedComment }
                  });
                }}
              >
                Reject
              </Button>
            ) : null
          }
        />

        <Box sx={{ height: 1 }} />
      </DialogContent>
    </Dialog>
  );
};
